import type { FeatureContext } from './featureContext';
import { initBlood } from '../blood';
import { BLOOD_ART_COUNT } from '../sim/content';
import type { SplatRoll } from '../render/bloodLayer';

/** Injectable seams for unit tests; defaults are the real modules. */
export interface BloodInstallDeps {
  initBlood?: typeof initBlood;
}

/**
 * Blood splatter (PRD #372): the client-only "Show blood" setting (its own
 * checkbox + localStorage key, gating the decal layer), the server-rolled scatter
 * broadcast (Slice B - every viewer draws the identical splats), and the
 * `[bloodtest` / `window.__blood()` dev hook.
 */
export function installBlood(ctx: FeatureContext, deps: BloodInstallDeps = {}): void {
  // A live toggle drives the current renderer; the persisted choice is
  // (re)applied to each fresh renderer below (scene swaps rebuild it).
  const bloodSetting = (deps.initBlood ?? initBlood)({
    onChange: (show) => ctx.renderer()?.setBloodEnabled(show),
  });
  ctx.onRendererBuilt((renderer) => renderer.setBloodEnabled(bloodSetting.isEnabled()));

  // The server rolls one scatter and broadcasts it, so every viewer draws the
  // identical splats. A hidden tab skips it (the rAF-driven decal layer would only
  // age it late); it is a silent 3-second ground decal, no audio. The `hue` seam
  // rides through unused.
  ctx.events.on('bloodFx', (msg) => {
    if (!ctx.ui.hidden) ctx.renderer()?.splatBlood({ x: msg.x, y: msg.y }, msg.splats, msg.hue);
  });

  /**
   * DEV blood-splatter hook (Slice A): roll a local scatter and spray it on the
   * player's tile through the same `splatBlood` primitive the wire message and
   * arena cascade drive - so a captain can eyeball the decals end-to-end with stub
   * art before any server caller exists. Rolls 1-3 splats in the 3x3 box
   * (Chebyshev 1), each a random one of the 7 blood arts.
   */
  const sprayBloodAtPlayer = (): number => {
    const renderer = ctx.renderer();
    const playerId = ctx.playerId();
    if (playerId === undefined || !renderer) return 0;
    const me = ctx.world()?.get(playerId)?.mobile;
    if (!me) return 0;
    const count = 1 + Math.floor(Math.random() * 3); // 1..3
    const splats: SplatRoll[] = [];
    for (let i = 0; i < count; i++) {
      splats.push({
        dx: Math.floor(Math.random() * 3) - 1,
        dy: Math.floor(Math.random() * 3) - 1,
        art: Math.floor(Math.random() * BLOOD_ART_COUNT),
      });
    }
    renderer.splatBlood({ x: me.tileX, y: me.tileY }, splats);
    // Return the live splat count so the dev/headless caller can confirm the spray.
    return renderer.bloodLayer.activeCount;
  };
  (window as unknown as { __blood?: () => number }).__blood = sprayBloodAtPlayer;

  // `[bloodtest` is the chat-command spelling of the same dev hook: never
  // broadcasts, exactly like `/dress`.
  ctx.chatCommands.intercept((text) => {
    if (text.trim().toLowerCase() !== '[bloodtest') return false;
    sprayBloodAtPlayer();
    return true;
  });
}
